import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

export function JivaSevaApp() {
  return (
    <PageShell>
      <Seo
        title="JivaSeva Services · JivaSeva"
        description="Explore JivaSeva Services — including JivaSeva Connect for volunteer coordination, JivaSeva Giving for small charitable donations, and JivaSeva Care for compassionate visits."
        path="/app"
      />
      <div className="page-hero">
        <h1 className="page-hero-name">JivaSeva Services</h1>
        <p className="page-hero-tagline">Turning Compassion Into Action</p>
      </div>

      <section className="about-section">
        <p className="manifesto">
          Ideas become meaningful when they inspire action. JivaSeva Services are the practical ways in which JivaSeva's mission comes to life. Each service is designed to remove practical barriers to compassionate action and make it easier for individuals, volunteers, and organizations to serve their communities. Some initiatives are already taking shape. Others are still ideas waiting to grow.
        </p>
      </section>

      <h2 className="services-category">Services in Development</h2>

      <section className="service-list">
        <article className="service-card">
          <h3 className="service-card-name">JivaSeva Connect</h3>
          <p className="service-card-tagline">Bringing Volunteers Together</p>
          <p className="service-card-summary">
            Organize volunteers, events, and community service activities.
          </p>
          <p className="service-card-description">
            JivaSeva Connect helps volunteer groups, non-profit organizations,
            temples, community associations, and local initiatives organize
            and coordinate service activities. From recruiting volunteers and
            managing registrations to scheduling events and communicating
            with teams, it provides the tools needed to bring people together
            in meaningful service.
          </p>
          <details>
            <summary className="service-card-toggle">Learn More</summary>
            <div className="service-card-details">
              <p className="callout">
                💡 Connecting People Who Wish to Serve
              </p>
              <p className="manifesto">
                Many temples, charitable organizations, community groups, and
                social service initiatives depend on volunteers, yet
                coordinating these efforts is often done through phone calls,
                spreadsheets, or messaging groups.
              </p>
              <p className="manifesto">
                JivaSeva Connect will provide a simple application where
                organizations can:
              </p>
              <ul className="bullet-list">
                <li>Publish volunteer opportunities.</li>
                <li>Create service activities and individual tasks.</li>
                <li>
                  Specify schedules, locations, contact information, and
                  requirements.
                </li>
                <li>Coordinate volunteers before and during events.</li>
                <li>Track participation and task completion.</li>
              </ul>
              <p className="manifesto">
                Volunteers will be able to discover opportunities aligned
                with their interests, register for activities, receive
                updates, communicate with organizers, and contribute their
                time more effectively.
              </p>
              <p className="manifesto">
                This service will be supported through an easy-to-use mobile
                application.
              </p>
            </div>
          </details>
        </article>
        <article className="service-card">
          <h3 className="service-card-name">JivaSeva Giving</h3>
          <p className="service-card-tagline">Supporting the Joy of Giving</p>
          <p className="service-card-summary">
            Enable small acts of giving in communities, places of worship,
            and public spaces.
          </p>
          <p className="service-card-description">
            JivaSeva Giving helps preserve the tradition of everyday
            generosity by making small acts of giving simple, transparent,
            and accessible. This initiative aims to address the challenge of
            obtaining small denomination notes or coins for cash donations in an increasingly
            digital world, and thus support the Joy of Giving.
          </p>
          <details>
            <summary className="service-card-toggle">Learn More</summary>
            <div className="service-card-details">
              <p className="callout">
                💡 Empowering Acts of Giving Small Donations
              </p>
              <p className="manifesto">
                JivaSeva Giving is an initiative that promotes charitable
                giving at temples, pilgrimage sites, and community spaces.
              </p>
              <p className="manifesto">
                Many people wish to offer small cash donations to those in
                need at temples, pilgrimage sites, and community spaces.
                Traditionally these acts of generosity depended on carrying
                loose change. Today, as society moves toward digital
                payments, many of us no longer carry cash—or when we do, we
                rarely have small denominations. As a result, the
                opportunity to give is often lost, even when the desire to
                help is present.
              </p>
              <p className="manifesto">
                JivaSeva Giving seeks to preserve and encourage these
                everyday acts of generosity through a gradual three-phase
                approach.
              </p>
              <p className="manifesto">
                The first phase of JivaSeva Giving—Assisted Giving—is
                planned for the near term.
              </p>

              <h4 className="subsection-heading">
                Phase 1 – Assisted Giving
              </h4>
              <p className="manifesto">
                JivaSeva volunteers operate service stalls or kiosks at
                participating locations. Visitors make a UPI payment to
                JivaSeva and receive the equivalent value in small
                denomination currency, enabling them to immediately offer
                assistance to those in need.
              </p>

              <h4 className="subsection-heading">
                Phase 2 – JivaSeva Vouchers
              </h4>
              <p className="manifesto">
                Instead of distributing physical currency, JivaSeva will
                introduce vouchers in various denominations. Donors purchase
                these vouchers at JivaSeva stalls or kiosks and hand them
                directly to individuals in need. Recipients can redeem the
                vouchers at participating JivaSeva stalls for their full
                value.
              </p>
              <p className="manifesto">
                This approach addresses the challenge of obtaining small
                denomination notes or coins. Beyond replacing physical
                currency, vouchers also create opportunities for
                partnerships with participating merchants who may choose to
                offer additional benefits or discounts, increasing the value
                received by those in need.
              </p>

              <h4 className="subsection-heading">
                Phase 3 – Fully Digital Giving
              </h4>
              <p className="manifesto">
                The long-term vision for JivaSeva Giving is a digitally
                enabled experience. Donors will be able to maintain a
                JivaSeva digital wallet (mobile app), configure preferred
                donation amounts, scan a recipient's QR code, and make
                anonymous donations instantly from within the app.
              </p>
              <p className="manifesto">
                Recipients can obtain a physical QR code at any JivaSeva
                stalls / kiosks. Recipients may optionally associate a
                photograph or other identifying information to help verify
                their identity if their physical QR code is lost or
                damaged. Recipients can visit any participating JivaSeva
                stalls to redeem the donations they have received.
              </p>
              <p className="manifesto">
                Throughout every phase, the objective remains the same: to
                make small acts of giving immediate, dignified, and
                accessible for everyone.
              </p>
            </div>
          </details>
        </article>

      </section>

      <h2 className="services-category">Planned Services</h2>

      <section className="service-list">

        <article className="service-card">
          <h3 className="service-card-name">JivaSeva Care</h3>
          <p className="service-card-tagline">
            Helping People Care for One Another
          </p>
          <p className="service-card-summary">
            Organize compassionate visits for people in need of
            companionship and community care.
          </p>
          <p className="service-card-description">
            JivaSeva Care helps volunteers and organizations coordinate
            visits and care activities for older adults, patients,
            children's homes, schools for the blind, care centers, and
            others who benefit from companionship and community support. It
            makes it easier to organize meaningful acts of care that bring
            comfort, connection, and dignity to people's lives.
          </p>
          <details>
            <summary className="service-card-toggle">Learn More</summary>
            <div className="service-card-details">
              <p className="manifesto">
                Every community has people who need support.
              </p>
              <ul className="bullet-list">
                <li>Elderly living alone.</li>
                <li>Children living in orphanage.</li>
                <li>Sick people in hospitals.</li>
                <li>Differently abled needing assistance.</li>
              </ul>
              <p className="manifesto">
                Communities also possess remarkable strengths — people
                willing to help.
              </p>
              <p className="manifesto">
                JivaSeva seeks to strengthen these connections by creating
                practical ways for communities to care for one another.
              </p>
            </div>
          </details>
        </article>
      </section>

      <h2 className="services-category">Looking Ahead</h2>

      <section className="about-section">
        <p className="manifesto">
          These are only the first of many services envisioned under JivaSeva.
        </p>
        <p className="manifesto">
          As new needs emerge, JivaSeva Services will continue to evolve, creating
          new opportunities for individuals, communities, and organizations to
          express compassion through meaningful action.
        </p>
        <p className="manifesto">
          Though the services may evolve, the guiding principle remains unchanged:{" "}
          <strong>Life Takes Care of Life</strong>.
        </p>
      </section>
    </PageShell>
  );
}
